import { getColumnTrack } from "@design-system/ViewTable/core/getGridTemplateColumns";

function readBalancedGroup(template: string, startIndex: number): number {
    let index = startIndex;
    let depth = 0;
    for (; index < template.length; index += 1) {
        const character = template[index];
        if (character === "(") {
            depth += 1;
        } else if (character === ")") {
            depth -= 1;
            if (depth === 0) {
                return index + 1;
            }
        }
    }
    return index;
}

function expandRepeatTracks(repeatToken: string): string[] | undefined {
    if (!repeatToken.startsWith("repeat(") || !repeatToken.endsWith(")")) {
        return undefined;
    }
    const inside = repeatToken.slice("repeat(".length, -1);
    const commaIndex = inside.indexOf(",");
    if (commaIndex < 0) {
        return undefined;
    }
    const repeatCount = Number.parseInt(inside.slice(0, commaIndex).trim(), 10);
    if (!Number.isFinite(repeatCount) || repeatCount < 1) {
        return undefined;
    }
    const innerTracks = tokenizeGridTemplateTracks(
        inside.slice(commaIndex + 1),
    );
    return Array.from({ length: repeatCount }, () => innerTracks).flat();
}

export function tokenizeGridTemplateTracks(
    gridTemplateColumns: string,
): string[] {
    const tracks: string[] = [];
    const template = gridTemplateColumns.trim();
    let index = 0;
    while (index < template.length) {
        while (template[index] === " ") {
            index += 1;
        }
        if (index >= template.length) {
            break;
        }
        if (
            template.startsWith("minmax(", index) ||
            template.startsWith("repeat(", index)
        ) {
            const start = index;
            index = readBalancedGroup(template, index);
            const token = template.slice(start, index);
            const expandedRepeatTracks = expandRepeatTracks(token);
            if (expandedRepeatTracks) {
                tracks.push(...expandedRepeatTracks);
            } else {
                tracks.push(token);
            }
            continue;
        }
        const start = index;
        while (index < template.length && template[index] !== " ") {
            index += 1;
        }
        tracks.push(template.slice(start, index));
    }
    return tracks;
}

export function getSpreadsheetGridTemplateColumns({
    defaultGridTemplateColumns,
    columnIds,
    columnSizing,
    userResizedColumnIds,
    minSize,
    maxSize,
    activelyResizingColumnId,
    columns,
}: {
    defaultGridTemplateColumns: string;
    columnIds: string[];
    columnSizing: Record<string, number>;
    userResizedColumnIds: ReadonlySet<string>;
    minSize: number;
    maxSize?: number;
    activelyResizingColumnId?: string | null;
    columns?: Array<{ minSize?: number; maxSize?: number }>;
}): string {
    const tracks = tokenizeGridTemplateTracks(defaultGridTemplateColumns);
    // The last flexible (fr-bearing) track keeps absorbing leftover space even
    // after a user resize. Otherwise resizing every flexible column turns all
    // tracks into fixed px; when their sum is narrower than the container the
    // grid leaves a dead gap on the right, detaching right-pinned columns
    // (sticky only engages on overflow) from the table edge.
    let lastFlexibleTrackIndex = -1;
    columnIds.forEach((columnId, index) => {
        if ((tracks[index] ?? "1fr").includes("1fr")) {
            lastFlexibleTrackIndex = index;
        }
    });
    return columnIds
        .map((columnId, index) => {
            const column = columns?.[index];
            const resolvedMinSize = column?.minSize ?? minSize;
            const resolvedMaxSize = column?.maxSize ?? maxSize;
            const columnSize = columnSizing[columnId];
            const isUserResized = userResizedColumnIds.has(columnId);
            const defaultTrack = tracks[index] ?? "1fr";

            if (isUserResized && columnSize !== undefined) {
                const isActivelyResizing =
                    activelyResizingColumnId === columnId;
                const track = getColumnTrack({
                    minSize: resolvedMinSize,
                    maxSize: resolvedMaxSize,
                    columnSize,
                    isFiller: false,
                    isUserResized: true,
                    isActivelyResizing,
                });
                if (
                    index === lastFlexibleTrackIndex &&
                    !isActivelyResizing &&
                    track.endsWith("px")
                ) {
                    return `minmax(${track}, 1fr)`;
                }
                return track;
            }

            if (defaultTrack === "1fr") {
                return getColumnTrack({
                    minSize: resolvedMinSize,
                    maxSize: resolvedMaxSize,
                    columnSize: columnSize ?? 0,
                    isFiller: false,
                    isUserResized: false,
                    isActivelyResizing: false,
                });
            }

            return defaultTrack;
        })
        .join(" ");
}
